import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, ChevronLeft, ChevronRight, ShieldCheck, Award, Factory,
  Truck, Wrench, FileText, Calendar, CheckCircle2,
  Bolt, Activity, ArrowUpRight, Play, Flame, Eye, Camera, Users, BadgeCheck,
  TrendingDown,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { useRFQ } from "../components/RFQProvider";
import api from "../lib/api";
import Reveal from "../components/Reveal";
import Counter from "../components/Counter";
import { HERO_GALLERY } from "../lib/brand";

const INDUSTRIES = [
  { icon: Flame, label: "Fire Suppression", count: "AFDSS" },
  { icon: Eye, label: "Driver Alert", count: "DMS / ADAS" },
  { icon: ShieldCheck, label: "Anti-Collision", count: "PWS / Radar" },
  { icon: Camera, label: "Camera Systems", count: "MDVR / 360°" },
  { icon: Bolt, label: "Vehicle Electricals", count: "Beacons / TPMS" },
  { icon: Truck, label: "Operator Safety", count: "SBRS / LDWS" },
];

/* WHY CHOOSE US — derived directly from About content */
const WHY = [
  {
    icon: Award,
    title: "Founded 2016 · KG Halli, Bangalore",
    text: "Established as an Electronics R&D and Mining Safety Equipment Trading + Service Company — backed by years of hands-on field expertise.",
  },
  {
    icon: ShieldCheck,
    title: "Mining Safety Specialisation",
    text: "Focused range — AFDSS, Audio Visual Alarm, Seat Belt Alarm, Body Lifting Alarm, Anti-Tilt Warning, Rear View Camera, Driver Fatigue device and more.",
  },
  {
    icon: Bolt,
    title: "E&E Solutions Expertise",
    text: "VFDs, Work Lamps, LIDAR & Ultrasonic Proximity Systems — designed and supplied by a technically deep electronics R&D team.",
  },
  {
    icon: Users,
    title: "Skilled, Dedicated Team",
    text: "A cohesive team of technically skilled engineers committed to value-based solutions and long-term customer success.",
  },
  {
    icon: BadgeCheck,
    title: "Quality, End to End",
    text: "From product selection and supply to installation, calibration, and post-sale service — every device is documented and traceable.",
  },
  {
    icon: TrendingDown,
    title: "Competitive Pricing",
    text: "Pricing engineered to suit customer budgets without compromising on safety, certification, or after-sales support.",
  },
];

const STATS = [
  { value: 9, suffix: "+", label: "Years in Industry", icon: Award },
  { value: 2400, suffix: "+", label: "Vehicles Equipped", icon: Truck },
  { value: 240, suffix: "T", label: "Heaviest HEMM Class", icon: Bolt },
  { value: 14, suffix: "", label: "Mining States Covered", icon: Activity },
];

const CLIENT_LOGOS = [
  "COAL INDIA", "NMDC", "NTPC", "TATA STEEL", "JSW STEEL", "ADANI",
  "BHEL", "L&T", "VEDANTA", "RELIANCE", "JINDAL", "BIRLA",
];

export default function HomePage() {
  const [slide, setSlide] = useState(0);
  const [featured, setFeatured] = useState([]);
  const [news, setNews] = useState([]);
  const { openRFQ } = useRFQ();

  useEffect(() => {
    const t = setInterval(() => setSlide((s) => (s + 1) % HERO_GALLERY.length), 5500);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    api.get("/products", { params: { featured: true } }).then((r) => setFeatured(r.data.slice(0, 6)));
    api.get("/news").then((r) => setNews(r.data.slice(0, 6)));
  }, []);

  const cur = HERO_GALLERY[slide];

  return (
    <div data-testid="home-page" className="bg-white">
      {/* HERO — full-width premium slideshow (no video) */}
      <section
        data-testid="hero-section"
        className="relative w-full overflow-hidden bg-carbon-950 min-h-[640px] h-[88vh] max-h-[860px]"
      >
        {/* Slideshow images */}
        {HERO_GALLERY.map((g, i) => (
          <div
            key={i}
            className={`absolute inset-0 transition-opacity duration-[1100ms] ease-out ${i === slide ? "opacity-100" : "opacity-0"}`}
            aria-hidden={i !== slide}
          >
            <img
              src={g.image}
              alt={g.thumbLabel}
              className={`w-full h-full object-cover ${i === slide ? "hero-zoom" : ""}`}
              draggable="false"
            />
          </div>
        ))}

        {/* Dark gradient & pattern overlays */}
        <div className="absolute inset-0 bg-gradient-to-r from-carbon-950/95 via-carbon-950/75 to-carbon-950/30" />
        <div className="absolute inset-0 bg-grid opacity-50" />
        <div className="absolute -top-32 -left-32 w-[420px] h-[420px] bg-brand-500/30 rounded-full blur-3xl" />
        <div className="absolute -bottom-20 right-0 w-[600px] h-[420px] bg-amber-500/10 rounded-full blur-3xl" />

        <div className="relative z-10 h-full max-w-[1600px] 2xl:max-w-[1760px] mx-auto px-5 md:px-8 grid lg:grid-cols-12 gap-8 items-center pt-6 pb-24">
          {/* Left content (re-mounts on slide change for animation) */}
          <div key={slide} className="lg:col-span-8 soft-up">
            <div className="flex items-center gap-3 mb-5">
              <div className="h-px w-12 bg-amber-400" />
              <div className="text-amber-400 text-[10.5px] sm:text-xs font-bold tracking-[0.3em] uppercase font-mono">
                {cur.overline}
              </div>
            </div>
            <h1 className="h-display text-white mb-6">{cur.title}</h1>
            <p className="body-lead text-slate-200 max-w-2xl mb-8">{cur.subtitle}</p>
            <div className="flex flex-wrap gap-3">
              <Button
                data-testid="hero-rfq-btn"
                onClick={() => openRFQ()}
                className="h-13 px-7 rounded-full btn-amber font-bold uppercase tracking-[0.18em] text-xs sm:text-sm"
              >
                Request a Quotation <ArrowRight className="w-4 h-4 ml-3" />
              </Button>
              <Link
                to="/products"
                data-testid="hero-products-link"
                className="h-13 px-7 inline-flex items-center font-bold uppercase tracking-[0.18em] text-xs sm:text-sm bg-white/10 hover:bg-white/20 text-white border border-white/30 backdrop-blur-sm transition-colors rounded-full"
              >
                Browse Products
              </Link>
            </div>

            <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-5 max-w-2xl">
              {[
                { v: "DGMS", l: "Compliant" },
                { v: "ISO", l: "9001:2015" },
                { v: "4HR", l: "SLA" },
                { v: "9+", l: "Years" },
              ].map((b, i) => (
                <div key={i} className="border-l-2 border-amber-500 pl-3">
                  <div className="font-heading font-extrabold text-2xl text-white leading-none">{b.v}</div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold mt-1">{b.l}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Right thumbs panel removed for cinematic clean look (no numbered boxes) */}
        </div>

        {/* Minimal centered dots — no numbers */}
        <div className="absolute bottom-20 left-1/2 -translate-x-1/2 z-10 flex items-center gap-2">
          {HERO_GALLERY.map((_, i) => (
            <button
              key={i}
              onClick={() => setSlide(i)}
              data-testid={`hero-dot-${i}`}
              className={`h-1.5 rounded-full transition-all ${i === slide ? "bg-amber-400 w-8" : "bg-white/30 hover:bg-white/60 w-1.5"}`}
              aria-label={`Slide ${i + 1}`}
            />
          ))}
        </div>

        {/* Side prev/next */}
        <div className="absolute bottom-20 right-5 z-10 flex items-center gap-2">
          <button
            onClick={() => setSlide((s) => (s - 1 + HERO_GALLERY.length) % HERO_GALLERY.length)}
            className="w-10 h-10 grid place-items-center bg-white/10 hover:bg-amber-500 hover:text-carbon-950 text-white border border-white/30 transition-colors rounded-full backdrop-blur-sm"
            data-testid="hero-prev"
            aria-label="Previous"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            onClick={() => setSlide((s) => (s + 1) % HERO_GALLERY.length)}
            className="w-10 h-10 grid place-items-center bg-white/10 hover:bg-amber-500 hover:text-carbon-950 text-white border border-white/30 transition-colors rounded-full backdrop-blur-sm"
            data-testid="hero-next"
            aria-label="Next"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>

        {/* Bottom marquee */}
        <div className="absolute bottom-0 left-0 right-0 z-10 bg-amber-500 overflow-hidden">
          <div className="flex marquee-track py-3 gap-12 whitespace-nowrap">
            {[...Array(2)].flatMap((_, j) =>
              [
                "★ MINING SAFETY SPECIALIST",
                "★ PAN-INDIA SERVICE NETWORK",
                "★ 24×7 EMERGENCY RESPONSE",
                "★ E&E SOLUTIONS PARTNER",
                "★ SKILLED ENGINEERING TEAM",
                "★ FOUNDED 2016 · BANGALORE",
              ].map((t, i) => (
                <span key={`${j}-${i}`} className="text-carbon-950 text-[11px] font-black tracking-widest font-mono">
                  {t}
                </span>
              ))
            )}
          </div>
        </div>
      </section>

      {/* STATS COUNTERS */}
      <section className="bg-carbon text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-20" />
        <div className="relative max-w-[1600px] 2xl:max-w-[1760px] mx-auto px-5 md:px-8 py-14 md:py-16 grid grid-cols-2 md:grid-cols-4 gap-px bg-steel-700/30">
          {STATS.map((s, i) => {
            const Icon = s.icon;
            return (
              <Reveal key={i} delay={i * 80}>
                <div className="bg-carbon p-6 md:p-7 group hover:bg-steel-800 transition-colors h-full">
                  <Icon className="w-7 h-7 text-amber-400 mb-4 group-hover:scale-110 transition-transform" strokeWidth={2} />
                  <div className="font-heading font-extrabold text-4xl md:text-5xl lg:text-6xl text-white leading-none">
                    <Counter to={s.value} suffix={s.suffix} />
                  </div>
                  <div className="text-[10px] md:text-xs uppercase tracking-widest text-slate-400 font-bold mt-3 font-mono">
                    {s.label}
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* COMPANY INTRO */}
      <section className="max-w-[1600px] 2xl:max-w-[1760px] mx-auto px-5 md:px-8 py-20 md:py-28 grid lg:grid-cols-12 gap-10 lg:gap-14">
        <Reveal className="lg:col-span-5">
          <div className="overline mb-3">About Universal EE</div>
          <h2 className="h-section text-carbon">
            Founded 2016 in Bangalore. Engineered for Indian mining.
          </h2>
        </Reveal>
        <Reveal delay={150} className="lg:col-span-7 space-y-5 text-slate-700 leading-relaxed text-base md:text-lg">
          <p>
            Universal Electricals &amp; Electronics is an <strong>Electronics R&amp;D and Mining
            Safety Equipment Trading and Service company</strong>, founded in <strong>2016 in KG
            Halli, Bangalore</strong>.
          </p>
          <p>
            As an E&amp;E solutions specialist, our portfolio covers <strong>AFDSS, Audio
            Visual Alarms, Seat Belt &amp; Body Lifting Alarms, Anti-Tilt Warning Devices,
            Rear-View Camera Systems, Driver Fatigue devices</strong> &mdash; plus control
            products such as <strong>Variable Frequency Drives, Work Lamps, LIDAR / Ultrasonic
            Proximity Systems</strong>.
          </p>
          <Link
            to="/about"
            className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.18em] text-amber-600 hover:text-amber-700 group"
            data-testid="home-learn-more"
          >
            Read full company story
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </Reveal>
      </section>

      {/* PRODUCTS — premium grid */}
      <section className="bg-slate-100 py-20 md:py-24 relative" data-testid="featured-products-section">
        <div className="max-w-[1600px] 2xl:max-w-[1760px] mx-auto px-5 md:px-8">
          <Reveal>
            <div className="flex items-end justify-between mb-10 md:mb-12 flex-wrap gap-4">
              <div>
                <div className="overline mb-2">Our Range</div>
                <h2 className="h-section text-carbon">Products</h2>
              </div>
              <Link to="/products" className="text-sm font-bold uppercase tracking-[0.18em] text-carbon hover:text-amber-600 inline-flex items-center gap-2 group">
                View full catalog
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </Reveal>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {featured.map((p, i) => {
              const cover = (p.images && p.images[0]) || p.image_url;
              return (
                <Reveal key={p.id} delay={i * 80}>
                  <Link
                    to={`/products/${p.id}`}
                    data-testid={`featured-product-${p.id}`}
                    className="group card-premium block h-full overflow-hidden"
                  >
                    <div className="aspect-[4/3] zoom-wrap relative bg-slate-100">
                      {cover ? (
                        <img src={cover} alt={p.name} className="w-full h-full object-cover zoom-img" />
                      ) : (
                        <div className="w-full h-full grid place-items-center text-slate-400 text-xs">No image</div>
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-carbon-950/70 via-transparent to-transparent" />
                      <div className="absolute top-3 left-3 bg-amber-500 text-carbon-950 px-2.5 py-1 text-[9px] font-black tracking-widest uppercase rounded-sm">
                        ★ Featured
                      </div>
                      <div className="absolute bottom-3 left-3 right-3">
                        <div className="text-[10px] tracking-[0.2em] text-amber-400 font-bold uppercase font-mono">{p.category}</div>
                      </div>
                    </div>
                    <div className="p-5">
                      <div className="h-card text-carbon mb-1 group-hover:text-amber-600 transition-colors">{p.name}</div>
                      <p className="text-xs sm:text-sm text-slate-600 line-clamp-2 leading-relaxed">{p.short_description}</p>
                      <div className="mt-4 inline-flex items-center gap-1 text-xs font-bold uppercase tracking-[0.18em] text-carbon group-hover:text-amber-600">
                        View details <ArrowUpRight className="w-3 h-3 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                      </div>
                    </div>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US — refreshed company-based content (6 cards) */}
      <section className="max-w-[1600px] 2xl:max-w-[1760px] mx-auto px-5 md:px-8 py-20 md:py-28">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14">
          <Reveal className="lg:col-span-4">
            <div className="overline mb-3">Why Choose Us</div>
            <h2 className="h-section text-carbon">A focused, technical, value-driven partner.</h2>
            <p className="mt-5 text-slate-600 leading-relaxed max-w-md">
              Our team is composed of technically skilled and highly dedicated personnel
              working cohesively to promote value-based solutions with a competitive pricing
              structure to suit customer needs.
            </p>
            <div className="mt-7 flex items-center gap-2 text-amber-600 font-bold text-sm uppercase tracking-[0.18em]">
              <span className="w-2 h-2 bg-emerald-500 rounded-full pulse-dot" />
              Live service network
            </div>
          </Reveal>
          <div className="lg:col-span-8 grid sm:grid-cols-2 gap-px bg-slate-200">
            {WHY.map((w, i) => {
              const Icon = w.icon;
              return (
                <Reveal key={i} delay={i * 80}>
                  <div className="bg-white p-6 md:p-7 hover:bg-slate-50 transition-colors group h-full relative overflow-hidden">
                    <div className="absolute -right-8 -top-8 w-24 h-24 bg-amber-500/5 rounded-full group-hover:bg-amber-500/10 transition-colors" />
                    <div className="relative">
                      <div className="w-12 h-12 bg-amber-500 grid place-items-center mb-4 group-hover:bg-carbon transition-colors rounded">
                        <Icon className="w-6 h-6 text-carbon-950 group-hover:text-amber-400 transition-colors" strokeWidth={2.2} />
                      </div>
                      <h3 className="h-card text-carbon mb-1.5">{w.title}</h3>
                      <p className="text-sm text-slate-600 leading-relaxed">{w.text}</p>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* DOMAIN — dark immersive */}
      <section className="bg-carbon-950 text-white py-20 md:py-28 relative overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-30" />
        <img
          src="https://images.pexels.com/photos/11347699/pexels-photo-11347699.jpeg?auto=compress&cs=tinysrgb&w=1920"
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-20"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-carbon-950 via-carbon-950/80 to-carbon-950" />
        <div className="relative max-w-[1600px] 2xl:max-w-[1760px] mx-auto px-5 md:px-8">
          <Reveal>
            <div className="overline mb-3 text-amber-400">What We Engineer</div>
            <h2 className="h-section max-w-3xl">
              The full safety arsenal for India&rsquo;s heavy-mining fleet —
              <span className="text-amber-400"> precision-built, mine-tested, mission-critical.</span>
            </h2>
            <p className="mt-5 text-slate-300 max-w-2xl text-base md:text-lg leading-relaxed">
              Six integrated capability lines covering every blind spot, every fatigue moment, every fire risk.
              Engineered in Bangalore. Field-proven across India&rsquo;s toughest opencast and underground mines.
            </p>
          </Reveal>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-px bg-steel-700/30 mt-10">
            {INDUSTRIES.map((it, i) => {
              const I = it.icon;
              return (
                <Reveal key={i} delay={i * 80}>
                  <div className="bg-carbon-950 p-6 md:p-7 hover:bg-steel-800 transition-colors text-center group h-full border border-transparent hover:border-amber-500/30 relative">
                    <I className="w-10 h-10 text-amber-400 mx-auto mb-4 group-hover:scale-110 transition-transform" strokeWidth={1.7} />
                    <div className="text-[12px] sm:text-sm font-bold uppercase tracking-wider mb-1">{it.label}</div>
                    <div className="text-xs text-amber-400/70 font-mono">{it.count}</div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* CLIENTS — premium credibility band */}
      <section className="relative bg-gradient-to-b from-white via-slate-50 to-white py-16 md:py-20 border-y-2 border-slate-200 overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-[0.04]" />
        <div className="relative max-w-[1600px] 2xl:max-w-[1760px] mx-auto px-5 md:px-8">
          <Reveal>
            <div className="text-center max-w-3xl mx-auto mb-10 md:mb-12">
              <div className="inline-flex items-center gap-2 mb-4">
                <span className="h-px w-10 bg-amber-500" />
                <span className="overline text-amber-600">Industry Trust</span>
                <span className="h-px w-10 bg-amber-500" />
              </div>
              <h2 className="h-section text-carbon">
                Chosen by India&rsquo;s most demanding operators.
              </h2>
              <p className="mt-3 text-slate-600 text-base md:text-lg leading-relaxed">
                From Coal India subsidiaries to private mining majors and HEMM OEMs &mdash;
                fleets across every major coal belt run on Universal EE safety devices.
              </p>
            </div>
          </Reveal>
        </div>
        <div className="flex marquee-track gap-12 md:gap-16 whitespace-nowrap items-center">
          {[...CLIENT_LOGOS, ...CLIENT_LOGOS].map((c, i) => (
            <span
              key={i}
              className="text-slate-400 hover:text-amber-600 font-heading font-extrabold text-xl md:text-2xl lg:text-[1.7rem] tracking-[0.18em] transition-colors px-2 inline-flex items-center gap-3"
            >
              <span className="w-1.5 h-1.5 bg-amber-400/70 rounded-full" />
              {c}
            </span>
          ))}
        </div>
      </section>

      {/* NEWS */}
      <section className="max-w-[1600px] 2xl:max-w-[1760px] mx-auto px-5 md:px-8 py-20 md:py-28">
        <Reveal>
          <div className="flex items-end justify-between mb-10 md:mb-12 flex-wrap gap-4">
            <div>
              <div className="overline mb-2">Newsroom</div>
              <h2 className="h-section text-carbon">Latest updates</h2>
            </div>
            <Link to="/news" className="text-sm font-bold uppercase tracking-[0.18em] text-carbon hover:text-amber-600 inline-flex items-center gap-2 group">
              All news <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </Reveal>
        <div className="grid sm:grid-cols-2 gap-5 md:gap-6">
          {news.map((n, i) => (
            <Reveal key={n.id} delay={i * 80}>
              <Link to={`/news/${n.id}`} className="group block card-premium h-full bg-white overflow-hidden">
                <div className="aspect-[16/9] zoom-wrap bg-slate-100 relative">
                  {n.image_url ? (
                    <img src={n.image_url.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${n.image_url}` : n.image_url} alt={n.title} className="w-full h-full object-cover zoom-img" />
                  ) : (
                    <div className="w-full h-full grid place-items-center text-slate-400 text-xs">No image</div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-carbon-950/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
                <div className="p-5">
                  <div className="font-mono text-xs text-amber-600 mb-2 flex items-center gap-2 font-bold">
                    <Calendar className="w-3 h-3" /> {new Date(n.date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
                  </div>
                  <div className="h-card text-carbon group-hover:text-amber-600 line-clamp-2 transition-colors">{n.title}</div>
                  <p className="text-sm text-slate-600 mt-2 line-clamp-2 leading-relaxed">{n.summary}</p>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* RFQ CTA — premium dark band */}
      <section className="bg-carbon-950 py-20 md:py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-30" />
        <div className="absolute -top-32 -right-32 w-96 h-96 bg-amber-500/20 rounded-full blur-3xl" />
        <div className="absolute -bottom-32 -left-32 w-96 h-96 bg-brand-500/20 rounded-full blur-3xl" />
        <div className="relative max-w-[1600px] 2xl:max-w-[1760px] mx-auto px-5 md:px-8 grid lg:grid-cols-12 gap-8 items-center">
          <Reveal className="lg:col-span-7">
            <div className="text-xs tracking-[0.3em] font-bold text-amber-400 uppercase mb-3 font-mono">
              Ready to engineer your project?
            </div>
            <h3 className="h-section text-white max-w-3xl">Get a technical quotation within 24 hours.</h3>
          </Reveal>
          <Reveal delay={200} className="lg:col-span-5 flex flex-col gap-3">
            <Button data-testid="cta-rfq-btn" onClick={() => openRFQ()} className="h-14 rounded-full btn-amber px-7 font-bold uppercase tracking-[0.18em] justify-start text-xs sm:text-sm">
              <FileText className="w-5 h-5 mr-3" /> Request Quotation <ArrowRight className="w-5 h-5 ml-auto" />
            </Button>
            <Link to="/contact" className="h-14 px-7 inline-flex items-center justify-start font-bold uppercase tracking-[0.18em] text-xs sm:text-sm bg-white/10 hover:bg-white/20 text-white border border-white/30 transition-colors rounded-full">
              <CheckCircle2 className="w-5 h-5 mr-3" /> Talk to Engineering <ArrowRight className="w-5 h-5 ml-auto" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
